import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, getDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useNavigate } from 'react-router-dom';
import { Payment } from '../../../types/financial';
import { generateInvoicePDFBlobFromModel } from '../../../components/Financial/InvoicePDFGenerator';
import { buildInvoiceDisplayModel } from '../../../utils/invoiceModel';
import { blobToBase64 } from '../../../utils/invoiceEmail';

interface ReceivableRecord {
  id: string;
  registrationId: string;
  invoiceNumber: string;
  firstName: string;
  lastName: string;
  placeName: string;
  amountOwing: number;
}

interface OrphanedRecord {
  id: string;
  registrationId: string;
  invoiceNumber: string;
  firstName: string;
  lastName: string;
  placeName: string;
}

type SortField = 'firstName' | 'lastName' | 'placeName' | 'amountOwing';
type SortDirection = 'asc' | 'desc';

const calculateNights = (arrivalDate: any, departureDate: any): number => {
  if (!arrivalDate || !departureDate) return 0;
  const arrival = new Date(arrivalDate);
  const departure = new Date(departureDate);
  const timeDiff = departure.getTime() - arrival.getTime();
  const nights = Math.ceil(timeDiff / (1000 * 3600 * 24));
  return nights > 0 ? nights : 0;
};

const formatCurrencyCAD = (amount: number): string =>
  new Intl.NumberFormat('en-CA', { style: 'currency', currency: 'CAD' }).format(amount);

const REGISTRAR_EMAIL = 'registraruccma@gmail.com';

// Build the reminder email content. Uses the balance computed from the invoice
// model so the amount stated here is identical to the attached PDF's total.
// Intentionally omits any direct pay link (spam trigger).
const buildReminderEmail = (firstName: string, note: string, balance: number) => {
  const greeting = `Dear ${firstName || 'camper'},`;
  const notePart = note.trim();
  const balanceLine = `Your account currently shows a balance owing of ${formatCurrencyCAD(balance)}. You can pay this any time by logging into your account and paying by credit card, or by e-transfer to ${REGISTRAR_EMAIL}.`;
  const closingLine = `If you have any questions you can email me at that same address, or if this is during camp feel free to drop in and visit at the registrar's hut.`;
  const signature = 'Sincerely,\nUCCMA Registrar';

  const paragraphs = [greeting];
  if (notePart) paragraphs.push(notePart);
  paragraphs.push(balanceLine, closingLine, 'The current invoice is attached for your records.', signature);

  const escapeHtml = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  const html = `<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333;">${paragraphs
    .map((p) => `<p>${escapeHtml(p).replace(/\n/g, '<br>')}</p>`)
    .join('')}</div>`;

  const text = paragraphs.join('\n\n');

  return { subject: 'Berwick Camp - Outstanding Balance Reminder', html, text };
};

const AccountsReceivable: React.FC = () => {
  const [records, setRecords] = useState<ReceivableRecord[]>([]);
  const [orphanedRecords, setOrphanedRecords] = useState<OrphanedRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sortField, setSortField] = useState<SortField>('amountOwing');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [selectedRecords, setSelectedRecords] = useState<Set<string>>(new Set());
  const [campSettings, setCampSettings] = useState<any>(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderNote, setReminderNote] = useState('');
  const [preparingPreview, setPreparingPreview] = useState(false);
  const [previewFirstName, setPreviewFirstName] = useState('');
  const [previewBalance, setPreviewBalance] = useState(0);
  const [sending, setSending] = useState(false);
  const [sendProgress, setSendProgress] = useState<{ current: number; total: number } | null>(null);
  const navigate = useNavigate();

  // Calculate total using the same live calculation as Registration Details / Invoice,
  // so the balance here matches what admins see on the registration record.
  const calculateTotalAmount = (registration: any, settings: any): number => {
    let grandTotal = 0;

    // Accommodation fees
    if (registration?.financial?.accommodationFees?.assignedAccommodation && settings?.rates?.accommodation) {
      grandTotal += Number(registration.financial.accommodationFees.accommodationFee) || 0;
    }

    // Registration fees (recomputed from live rates and current campers/nights)
    if (Array.isArray(registration?.attendingCampers) && settings?.rates?.registration) {
      registration.attendingCampers.forEach((camper: any) => {
        const nights = camper.nights || calculateNights(registration?.campDates?.arrivalDate, registration?.campDates?.departureDate);
        const isFullCamp = nights >= 5;

        let rate = 0;
        if (camper.age === 'Adult' && settings.rates.registration.adult) {
          rate = isFullCamp ? (settings.rates.registration.adult.weekly || 0) : (settings.rates.registration.adult.nightly || 0) * nights;
        } else if (camper.age === 'AdultStudent' && settings.rates.registration.adultStudent) {
          rate = isFullCamp ? (settings.rates.registration.adultStudent.weekly || 0) : (settings.rates.registration.adultStudent.nightly || 0) * nights;
        } else if (camper.age === 'YouthChildren' && settings.rates.registration.youthAndChildren) {
          rate = isFullCamp ? (settings.rates.registration.youthAndChildren.weekly || 0) : (settings.rates.registration.youthAndChildren.nightly || 0) * nights;
        }
        grandTotal += rate;
      });
    }

    // Meal fees (recomputed from live rates) - check new path then legacy
    if (settings?.rates?.meals) {
      const fullCampAdultRate = settings.rates.meals.fullCampAdult || 0;
      const fullCampChildRate = settings.rates.meals.fullCampChild || 0;
      const dailyAdultRate = settings.rates.meals.dailyAdult || 0;
      const dailyChildRate = settings.rates.meals.dailyChild || 0;

      const meals = registration?.financial?.otherFees?.meals;
      if (meals) {
        grandTotal += (meals.fullCampPassesAdult?.quantity || 0) * fullCampAdultRate;
        grandTotal += (meals.fullCampPassesChild?.quantity || 0) * fullCampChildRate;
        grandTotal += (meals.dailyPassesAdult?.quantity || 0) * dailyAdultRate;
        grandTotal += (meals.dailyPassesChild?.quantity || 0) * dailyChildRate;
      } else {
        const legacyMeals = registration?.financial?.mealFees || registration?.mealFees;
        if (legacyMeals) {
          const fullCampAdult = legacyMeals.fullCampPassesAdult || legacyMeals.fullCampPasses?.adult || 0;
          const fullCampChild = legacyMeals.fullCampPassesChild || legacyMeals.fullCampPasses?.child || 0;
          const dailyAdult = legacyMeals.dailyPassesAdult || legacyMeals.dailyPasses?.adult || 0;
          const dailyChild = legacyMeals.dailyPassesChild || legacyMeals.dailyPasses?.child || 0;
          grandTotal += fullCampAdult * fullCampAdultRate;
          grandTotal += fullCampChild * fullCampChildRate;
          grandTotal += dailyAdult * dailyAdultRate;
          grandTotal += dailyChild * dailyChildRate;
        }
      }
    }

    // Other fees (fridge, donations, custom, array items)
    if (registration?.financial?.otherFees) {
      const otherFees = registration.financial.otherFees;
      grandTotal += Number(otherFees.fridge?.cost ?? otherFees.fridgeRental ?? 0) || 0;
      grandTotal += Number(otherFees.donations?.amount ?? otherFees.donation ?? 0) || 0;
      grandTotal += Number(otherFees.customFee?.amount) || 0;
      for (const fee of (otherFees.otherFees || [])) {
        grandTotal += Number(fee?.amount) || 0;
      }
    }

    // Enabled overrides
    const savedOverrides = registration?.financial?.overrides;
    if (savedOverrides?.accommodation?.enabled) {
      grandTotal += Number(savedOverrides.accommodation.amount) || 0;
    }
    if (savedOverrides?.registration?.enabled) {
      grandTotal += Number(savedOverrides.registration.amount) || 0;
    }
    if (savedOverrides?.mealFees?.enabled) {
      grandTotal += Number(savedOverrides.mealFees.amount) || 0;
    }
    if (savedOverrides?.otherFees?.enabled) {
      grandTotal += Number(savedOverrides.otherFees.amount) || 0;
    }

    return Math.round(grandTotal * 100) / 100; // Round to 2 decimal places
  };

  // Calculate remaining balance from total amount and payments
  const calculateRemainingBalance = (totalAmount: number, payments: Payment[]): number => {
    const paymentsTotal = Array.isArray(payments)
      ? payments.reduce((sum, payment) => {
          if (payment.amount && typeof payment.amount === 'number') {
            return sum + payment.amount;
          }
          return sum;
        }, 0)
      : 0;

    const remaining = totalAmount - paymentsTotal;
    return Math.round(remaining * 100) / 100; // Round to 2 decimal places
  };

  useEffect(() => {
    fetchReceivables();
  }, []);

  const fetchReceivables = async () => {
    try {
      setLoading(true);
      setError('');
      
      console.log('AR Report: Starting fetch...');
      const startTime = Date.now();
      
      // Step 1: Get all registrations and camp settings
      console.log('AR Report: Fetching registrations...');
      const [registrationsSnapshot, settingsSnap] = await Promise.all([
        getDocs(collection(db, 'Registrations')),
        getDoc(doc(db, 'settings', 'camp')),
      ]);
      const registrations = registrationsSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...(docSnap.data() as any) }));
      const settingsData = settingsSnap.exists() ? settingsSnap.data() : null;
      setCampSettings(settingsData);
      console.log(`AR Report: Found ${registrations.length} registrations`);
      
      const receivables: ReceivableRecord[] = [];
      const orphaned: OrphanedRecord[] = [];
      
      // Step 2: Process each registration
      console.log('AR Report: Starting processing of registrations...');
      const processStartTime = Date.now();
      
      for (const registration of registrations) {
        // Only include registrations with status "Assigned"
        if (registration.status !== 'Assigned') {
          continue;
        }
        
        const invoiceNumber = registration.financial?.invoiceNumber || registration.financialRecord?.invoiceNumber || registration.invoiceNumber || registration.invoice?.invoiceNumber;
        
        if (!invoiceNumber) {
          // No invoice number - add to orphaned records
          const primaryCamperName = registration.registrationInfo?.primaryCamperName || '';
          const nameParts = primaryCamperName.split(' ');
          const firstName = nameParts[0] || registration.firstName || '';
          const lastName = nameParts.slice(1).join(' ') || registration.lastName || '';
          
          orphaned.push({
            id: registration.id,
            registrationId: registration.id,
            invoiceNumber: '',
            firstName,
            lastName,
            placeName: registration.assignedAccommodation?.placeName || registration.accommodationFees?.placeName || registration.financial?.accommodationFees?.placeName || '',
          });
          continue;
        }
        
        // Calculate total using the live calculation (matches the registration record)
        const totalAmount = calculateTotalAmount(registration, settingsData);
        
        // Get payments from paymentHistory
        const payments = registration.paymentHistory || registration.payments || [];
        const remainingBalance = calculateRemainingBalance(totalAmount, payments);
        
        // Check if there's a remaining balance (non-zero)
        if (remainingBalance === 0) {
          continue; // Skip records with zero balance
        }
        
        // Extract name from different possible structures
        const primaryCamperName = registration.registrationInfo?.primaryCamperName || '';
        const nameParts = primaryCamperName.split(' ');
        const firstName = nameParts[0] || registration.firstName || '';
        const lastName = nameParts.slice(1).join(' ') || registration.lastName || '';
        
        // Add to receivables
        receivables.push({
          id: registration.id,
          registrationId: registration.id,
          invoiceNumber: invoiceNumber,
          firstName,
          lastName,
          placeName: registration.assignedAccommodation?.placeName || registration.accommodationFees?.placeName || registration.financial?.accommodationFees?.placeName || '',
          amountOwing: remainingBalance,
        });
      }
      
      const processEndTime = Date.now();
      console.log(`AR Report: Processing completed in ${processEndTime - processStartTime}ms. Found ${receivables.length} receivables and ${orphaned.length} orphaned records`);
      
      const totalTime = Date.now() - startTime;
      console.log(`AR Report: Total fetch time: ${totalTime}ms`);
      
      setRecords(receivables);
      setOrphanedRecords(orphaned);
    } catch (err) {
      console.error('Error fetching receivables:', err);
      setError('Failed to load accounts receivable records');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortedRecords = () => {
    return [...records].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleRowClick = (registrationId: string) => {
    if (registrationId) {
      navigate(`/admin/registration-details/${registrationId}`);
    }
  };

  const handleCheckboxChange = (recordId: string, checked: boolean) => {
    const newSelected = new Set(selectedRecords);
    if (checked) {
      newSelected.add(recordId);
    } else {
      newSelected.delete(recordId);
    }
    setSelectedRecords(newSelected);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedRecords(new Set(sortedRecords.map(record => record.id)));
    } else {
      setSelectedRecords(new Set());
    }
  };

  const getSelectedRecordsList = () => sortedRecords.filter(record => selectedRecords.has(record.id));

  // Prepare the reminder modal: preview the first selected camper's email using the
  // same invoice-model balance that will appear on the attached PDF.
  const openReminderModal = async () => {
    const selectedRecordsList = getSelectedRecordsList();
    if (selectedRecordsList.length === 0) {
      alert('No campers selected for reminders.');
      return;
    }

    setPreparingPreview(true);
    setShowReminderModal(true);
    try {
      const first = selectedRecordsList[0];
      const registrationDoc = await getDoc(doc(db, 'Registrations', first.registrationId));
      const registrationData = registrationDoc.exists()
        ? { id: registrationDoc.id, ...(registrationDoc.data() as any) }
        : null;

      const built = registrationData ? buildInvoiceDisplayModel(registrationData, campSettings) : null;
      setPreviewFirstName(first.firstName || built?.model?.primaryCamper?.firstName || '');
      setPreviewBalance(built ? built.balanceDue : first.amountOwing);
    } catch (error) {
      console.error('Error preparing reminder preview:', error);
      const first = selectedRecordsList[0];
      setPreviewFirstName(first.firstName || '');
      setPreviewBalance(first.amountOwing);
    } finally {
      setPreparingPreview(false);
    }
  };

  const closeReminderModal = () => {
    if (sending) return;
    setShowReminderModal(false);
    setReminderNote('');
    setSendProgress(null);
  };

  const sendReminders = async () => {
    const selectedRecordsList = getSelectedRecordsList();
    if (selectedRecordsList.length === 0) {
      alert('No campers selected for reminders.');
      return;
    }

    setSending(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      for (let i = 0; i < selectedRecordsList.length; i++) {
        const record = selectedRecordsList[i];
        setSendProgress({ current: i + 1, total: selectedRecordsList.length });

        try {
          const registrationDoc = await getDoc(doc(db, 'Registrations', record.registrationId));
          if (!registrationDoc.exists()) {
            console.error(`No registration found for ${record.registrationId}`);
            errorCount++;
            continue;
          }
          const registrationData = { id: registrationDoc.id, ...(registrationDoc.data() as any) };

          const built = buildInvoiceDisplayModel(registrationData, campSettings);
          if (!built || !built.recipientEmail) {
            console.error(`No email found for registration ${record.registrationId}`);
            errorCount++;
            continue;
          }

          const pdfBlob = await generateInvoicePDFBlobFromModel(built.model, built.invoiceNumber);
          const pdfBase64 = await blobToBase64(pdfBlob);

          const { subject, html, text } = buildReminderEmail(
            record.firstName || built.primaryCamperName,
            reminderNote,
            built.balanceDue
          );

          await addDoc(collection(db, 'emails'), {
            to: [{ email: built.recipientEmail.toLowerCase() }],
            from: { email: REGISTRAR_EMAIL, name: 'Berwick Camp' },
            subject,
            html,
            text,
            attachments: [
              {
                filename: `${built.invoiceNumber || 'invoice'}.pdf`,
                content: pdfBase64,
                contentType: 'application/pdf',
              },
            ],
            status: 'pending',
            createdAt: serverTimestamp(),
          });

          successCount++;
        } catch (error) {
          console.error(`Error sending reminder to ${record.firstName} ${record.lastName}:`, error);
          errorCount++;
        }
      }

      if (successCount > 0) {
        alert(`Successfully queued ${successCount} reminder email(s).${errorCount > 0 ? ` ${errorCount} failed.` : ''}`);
        setSelectedRecords(new Set());
        setShowReminderModal(false);
        setReminderNote('');
      } else {
        alert(`Failed to send any reminder emails. ${errorCount} error(s) occurred.`);
      }
    } catch (error) {
      console.error('Error sending reminders:', error);
      alert('An error occurred while sending reminders. Please try again.');
    } finally {
      setSending(false);
      setSendProgress(null);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(amount);
  };

  const exportToCSV = () => {
    const sortedRecords = getSortedRecords();
    
    // Create CSV header
    const headers = ['First Name', 'Last Name', 'Place Name', 'Invoice Number', 'Amount Owing'];
    
    // Create CSV rows
    const csvRows = [
      headers.join(','),
      ...sortedRecords.map(record => [
        `"${record.firstName}"`,
        `"${record.lastName}"`,
        `"${record.placeName}"`,
        `"${record.invoiceNumber}"`,
        record.amountOwing.toFixed(2)
      ].join(','))
    ];
    
    const csvContent = csvRows.join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `accounts-receivable-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportOrphanedToCSV = () => {
    // Create CSV header
    const headers = ['First Name', 'Last Name', 'Place Name', 'Invoice Number', 'Registration ID'];
    
    // Create CSV rows
    const csvRows = [
      headers.join(','),
      ...orphanedRecords.map(record => [
        `"${record.firstName}"`,
        `"${record.lastName}"`,
        `"${record.placeName}"`,
        `"${record.invoiceNumber}"`,
        `"${record.registrationId}"`
      ].join(','))
    ];
    
    const csvContent = csvRows.join('\n');
    
    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `orphaned-accommodation-assignments-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) return <div>Loading accounts receivable...</div>;
  if (error) return <div className="error">{error}</div>;

  const sortedRecords = getSortedRecords();
  const selectedCount = selectedRecords.size;
  const previewEmail = buildReminderEmail(previewFirstName, reminderNote, previewBalance);

  return (
    <div className="accounts-receivable-report">
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '20px'
      }}>
        <div>
          <h2>Accounts Receivable</h2>
          <p>List of all campers with a non-zero balance (including credits).</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          {sortedRecords.length > 0 && (
            <button 
              style={{
                padding: '8px 16px',
                background: '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.9rem',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#218838'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#28a745'}
              onClick={exportToCSV}
            >
              Export AR to CSV
            </button>
          )}
          {orphanedRecords.length > 0 && (
            <button 
              style={{
                padding: '8px 16px',
                background: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.9rem',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#c82333'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#dc3545'}
              onClick={exportOrphanedToCSV}
            >
              Export Orphaned to CSV
            </button>
          )}
          {selectedRecords.size > 0 && (
            <button 
              style={{
                padding: '8px 16px',
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500',
                fontSize: '0.9rem',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#0056b3'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#007bff'}
              onClick={openReminderModal}
            >
              Send Reminders ({selectedRecords.size})
            </button>
          )}
        </div>
      </div>
      <table className="receivable-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: 16 }}>
        <thead>
          <tr>
            <th style={{ width: '40px', textAlign: 'center' }}>
              <input
                type="checkbox"
                aria-label="Select all"
                title="Select all"
                checked={sortedRecords.length > 0 && selectedRecords.size === sortedRecords.length}
                ref={(el) => {
                  if (el) {
                    el.indeterminate = selectedRecords.size > 0 && selectedRecords.size < sortedRecords.length;
                  }
                }}
                onChange={(e) => handleSelectAll(e.target.checked)}
                style={{ cursor: 'pointer' }}
              />
            </th>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('firstName')}>
              First Name {sortField === 'firstName' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
            </th>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('lastName')}>
              Last Name {sortField === 'lastName' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
            </th>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('placeName')}>
              Place Name {sortField === 'placeName' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
            </th>
            <th>
              Invoice #
            </th>
            <th style={{ cursor: 'pointer' }} onClick={() => handleSort('amountOwing')}>
              Amount Owing {sortField === 'amountOwing' ? (sortDirection === 'asc' ? '↑' : '↓') : ''}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedRecords.map(record => (
            <tr
              key={record.id}
              style={{ cursor: 'pointer', borderBottom: '1px solid #ddd', padding: '8px' }}
              onClick={() => handleRowClick(record.registrationId)}
              title="Click to view accommodation assignment"
            >
              <td
                style={{ padding: '8px', textAlign: 'center' }}
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={selectedRecords.has(record.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleCheckboxChange(record.id, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  style={{ cursor: 'pointer' }}
                />
              </td>
              <td style={{ padding: '8px' }}>{record.firstName}</td>
              <td style={{ padding: '8px' }}>{record.lastName}</td>
              <td style={{ padding: '8px' }}>{record.placeName}</td>
              <td style={{ padding: '8px' }}>{record.invoiceNumber}</td>
              <td style={{ padding: '8px' }}>{formatCurrency(record.amountOwing)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {sortedRecords.length === 0 && <div>No accounts receivable records found.</div>}
      
      {/* Orphaned Records Section */}
      {orphanedRecords.length > 0 && (
        <div style={{ marginTop: '40px' }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '20px',
            padding: '16px',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '6px'
          }}>
            <div>
              <h3 style={{ color: '#856404', margin: 0 }}>⚠️ Orphaned Accommodation Assignments</h3>
              <p style={{ color: '#856404', margin: '8px 0 0 0', fontSize: '0.9rem' }}>
                {orphanedRecords.length} accommodation assignment(s) with missing or invalid invoice numbers. 
                These records could not be matched to financial records.
              </p>
            </div>
          </div>
          
          <table className="orphaned-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#fff3cd' }}>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ffeaa7' }}>First Name</th>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ffeaa7' }}>Last Name</th>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ffeaa7' }}>Place Name</th>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ffeaa7' }}>Invoice Number</th>
                <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #ffeaa7' }}>Registration ID</th>
              </tr>
            </thead>
            <tbody>
              {orphanedRecords.map(record => (
                <tr
                  key={record.id}
                  style={{ 
                    cursor: 'pointer', 
                    borderBottom: '1px solid #ffeaa7',
                    backgroundColor: '#fffbf0'
                  }}
                  onClick={() => handleRowClick(record.registrationId)}
                  title="Click to view accommodation assignment"
                >
                  <td style={{ padding: '8px' }}>{record.firstName}</td>
                  <td style={{ padding: '8px' }}>{record.lastName}</td>
                  <td style={{ padding: '8px' }}>{record.placeName}</td>
                  <td style={{ padding: '8px' }}>{record.invoiceNumber || 'Missing'}</td>
                  <td style={{ padding: '8px' }}>{record.registrationId}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showReminderModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            padding: '20px',
          }}
          onClick={closeReminderModal}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '8px',
              maxWidth: '640px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              padding: '24px',
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ marginTop: 0 }}>Send Balance Reminders</h3>
            <p style={{ color: '#555', marginTop: 0 }}>
              Sending to <strong>{selectedCount}</strong> camper(s). Each email includes a personalized
              note, the outstanding balance, and the invoice attached as a PDF.
            </p>

            <label style={{ display: 'block', fontWeight: 600, marginBottom: '6px' }}>
              Your note (added to every email in this batch):
            </label>
            <textarea
              value={reminderNote}
              onChange={(e) => setReminderNote(e.target.value)}
              disabled={sending}
              rows={4}
              placeholder="e.g. I hope you're enjoying camp this week! Just a friendly reminder about your account balance."
              style={{
                width: '100%',
                padding: '10px',
                borderRadius: '6px',
                border: '1px solid #ccc',
                fontFamily: 'inherit',
                fontSize: '0.95rem',
                boxSizing: 'border-box',
                resize: 'vertical',
              }}
            />

            <div style={{ marginTop: '16px' }}>
              <div style={{ fontWeight: 600, marginBottom: '6px' }}>
                Preview (first camper{previewFirstName ? `: ${previewFirstName}` : ''}):
              </div>
              <div
                style={{
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  padding: '16px',
                  background: '#fafafa',
                  fontSize: '0.9rem',
                }}
              >
                {preparingPreview ? (
                  <em>Preparing preview…</em>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: previewEmail.html }} />
                )}
              </div>
              <div style={{ marginTop: '8px', fontSize: '0.85rem', color: '#666' }}>
                📎 The camper's current invoice (PDF) will be attached. The balance shown above matches
                the attached invoice total.
              </div>
            </div>

            {sendProgress && (
              <div style={{ marginTop: '16px', fontWeight: 600, color: '#007bff' }}>
                Sending {sendProgress.current} of {sendProgress.total}…
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={closeReminderModal}
                disabled={sending}
                style={{
                  padding: '8px 16px',
                  background: '#e0e0e0',
                  color: '#333',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: sending ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                }}
              >
                Cancel
              </button>
              <button
                onClick={sendReminders}
                disabled={sending || preparingPreview}
                style={{
                  padding: '8px 16px',
                  background: sending || preparingPreview ? '#8ab7e8' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: sending || preparingPreview ? 'not-allowed' : 'pointer',
                  fontWeight: 500,
                }}
              >
                {sending ? 'Sending…' : `Send ${selectedCount} Reminder(s)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsReceivable; 